import { type ButtonVariant } from '#app/components/Buttons/types.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { type CollaboratorItem } from '#app/features/paidProjects/components/Proposal/ProposalForm/CollaboratorInput/types.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useContraPayments } from '#app/hooks/useContraPayments.js';
import { useNavigation } from '#app/hooks/useNavigation.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import { getRoute } from '@contra/routes';
import { useCallback } from 'react';

type StartProjectButtonProps = {
  readonly counterpartyUserProfile: CollaboratorItem | null;
  readonly jobId?: null | string;
  readonly size: 'small' | 'xsmall';
  readonly variant?: ButtonVariant;
};

export const useStartProject = (
  counterpartyUserProfile: StartProjectButtonProps['counterpartyUserProfile'],
  jobId?: StartProjectButtonProps['jobId'],
) => {
  const { selectedUserType } = useSelectedUserType();
  const navigation = useNavigation();
  const toast = useToast();

  const { initiateSetupPayments, userHasPaymentsSetup } = useContraPayments();
  const requirePaymentsOnboarding =
    selectedUserType === 'CONTRACTOR' && !userHasPaymentsSetup;

  const startProject = useCallback(() => {
    try {
      navigation.push(
        getRoute('/proposal/new', {
          search: {
            collaborator: counterpartyUserProfile?.displayUsername,
            jobId: jobId ?? undefined,
          },
        }),
      );
    } catch {
      toast.error('Oops! Failed to create new project. Please try again.');
    }
  }, [navigation, counterpartyUserProfile?.displayUsername, jobId, toast]);

  const { track } = useAnalytics();

  const onStartProject = () => {
    track('button', {
      action: 'tapped',
      button_name: 'work_start_paid_project',
      section: 'relationship_page',
    });

    if (requirePaymentsOnboarding) {
      initiateSetupPayments();
      return;
    }

    startProject();
  };

  return { onStartProject };
};
